(() => {
  const control = document.querySelector('[data-city-klaviyo-teaser-control]');
  const dismissControl = document.querySelector('[data-city-klaviyo-teaser-dismiss]');
  if (!control || !dismissControl || window.CityKlaviyoTeaserControl) return;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const dismissalKey = 'city_optics_klaviyo_teaser_dismissed_until_v3';
  const dismissalDuration = 14 * 24 * 60 * 60 * 1000;
  const autoCollapseDelay = 5000;
  const panelId = 'CityKlaviyoSignupPanel';
  const productIntent = control.dataset.cityKlaviyoProductIntent || '';
  const productFormIds = {
    mobile: control.dataset.cityKlaviyoMobileFormId || '',
    desktop: control.dataset.cityKlaviyoDesktopFormId || '',
  };
  const productOpenDelay = Number(control.dataset.cityKlaviyoProductOpenDelay) || 6000;
  const routedProductFormIds = new Set(Object.values(productFormIds).filter(Boolean));

  let teaser = null;
  let panel = null;
  let collapseTimer = null;
  let productOpenTimer = null;
  let collapseStarted = false;
  let autoCollapsed = false;
  let panelWasSeen = false;
  let panelCollapsedByControl = false;
  let collapsingViaControl = false;
  let focusPanelAfterOpen = false;
  let dismissed = false;
  let syncQueued = false;
  let returningToTeaser = false;
  let knownFormId = '';

  const keyboardFocusClass = 'city-klaviyo-keyboard-focus';
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Tab') document.documentElement.classList.add(keyboardFocusClass);
    },
    true
  );
  document.addEventListener(
    'pointerdown',
    () => document.documentElement.classList.remove(keyboardFocusClass),
    true
  );

  const normaliseText = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return styles.display !== 'none' && styles.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const readDismissal = () => {
    try {
      const dismissedUntil = Number(window.localStorage.getItem(dismissalKey));
      if (Number.isFinite(dismissedUntil) && dismissedUntil > Date.now()) return true;
      window.localStorage.removeItem(dismissalKey);
    } catch (_error) {
      // Fail open if storage is unavailable.
    }
    return false;
  };

  const writeDismissal = () => {
    try {
      window.localStorage.setItem(dismissalKey, String(Date.now() + dismissalDuration));
    } catch (_error) {
      // Klaviyo's own dismissal remains available if storage is unavailable.
    }
  };

  const findTeaser = () => {
    const candidates = [...document.querySelectorAll('[class*="kl-teaser-"]')].filter(
      (element) => !element.closest('[role="dialog"]')
    );
    return candidates.find(isVisible) || candidates[0] || null;
  };

  const findPanel = () =>
    [...document.querySelectorAll('[role="dialog"][aria-label="POPUP Form"]')].find(isVisible) || null;

  const findFormId = () => {
    const formTestId = document.querySelector('[data-testid^="klaviyo-form-"]')?.getAttribute('data-testid') || '';
    const formMatch = formTestId.match(/^klaviyo-form-([A-Za-z0-9]+)$/);
    if (formMatch) return formMatch[1];

    const teaserClass = teaser?.className || '';
    const teaserMatch = String(teaserClass).match(/(?:^|\s)kl-teaser-([A-Za-z0-9]+)(?:\s|$)/);
    return teaserMatch ? teaserMatch[1] : '';
  };

  const findTeaserTrigger = (element) => {
    if (!element) return null;
    const candidates = [...element.querySelectorAll('[role="button"], button, [tabindex="0"]')];
    return candidates.find((candidate) => candidate.getAttribute('aria-label') !== 'Close teaser') || element;
  };

  const sizeCloseTarget = (element) => {
    const closeButton = element?.querySelector('[aria-label="Close dialog"]');
    if (!closeButton) return null;

    const styles = window.getComputedStyle(closeButton);
    const rect = closeButton.getBoundingClientRect();
    const computedWidth = Number.parseFloat(styles.width);
    const computedHeight = Number.parseFloat(styles.height);
    const scaleX = computedWidth > 0 ? rect.width / computedWidth : 1;
    const scaleY = computedHeight > 0 ? rect.height / computedHeight : 1;
    const scale = Math.min(scaleX, scaleY);

    if (Number.isFinite(scale) && scale > 0 && Math.min(rect.width, rect.height) < 43.5) {
      const targetSize = Math.min(120, Math.ceil(44 / scale));
      const iconSize = Math.min(36, Math.ceil(14 / scale));
      closeButton.style.setProperty('--city-klaviyo-close-target', `${targetSize}px`);
      closeButton.style.setProperty('--city-klaviyo-close-icon', `${iconSize}px`);
    }

    return closeButton;
  };

  const clearCollapseTimer = () => {
    if (!collapseTimer) return;
    window.clearTimeout(collapseTimer);
    collapseTimer = null;
  };

  const clearProductOpenTimer = () => {
    if (!productOpenTimer) return;
    window.clearTimeout(productOpenTimer);
    productOpenTimer = null;
  };

  const productFormId = () => (mobileQuery.matches ? productFormIds.mobile : productFormIds.desktop);

  const scheduleProductIntentForm = () => {
    if (!productIntent || dismissed || readDismissal()) return;

    clearProductOpenTimer();
    productOpenTimer = window.setTimeout(() => {
      productOpenTimer = null;
      if (dismissed || readDismissal()) return;

      panel = findPanel();
      teaser = findTeaser();
      if ((panel && isVisible(panel)) || (teaser && isVisible(teaser))) return;

      const formId = productFormId();
      if (!formId) return;

      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    }, productOpenDelay);
  };

  const suppressTeaser = (shouldSuppress) => {
    teaser = findTeaser() || (teaser && teaser.isConnected ? teaser : null);
    if (!teaser) return;
    teaser.classList.toggle('city-klaviyo-teaser--suppressed', shouldSuppress);
    teaser.setAttribute('aria-hidden', shouldSuppress ? 'true' : 'false');
  };

  const hideControl = () => {
    control.hidden = true;
    dismissControl.hidden = true;
    control.setAttribute('aria-expanded', 'false');
  };

  const showControl = (expanded) => {
    control.hidden = false;
    dismissControl.hidden = false;
    control.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    control.setAttribute('aria-label', expanded ? 'Collapse signup offer' : 'Open signup offer');
  };

  const restoreCollapsedFocus = () => {
    const active = document.activeElement;
    const focusIsHidden =
      !active ||
      active === document.body ||
      (teaser && teaser.contains(active)) ||
      (panel && panel.contains(active) && !isVisible(panel));

    if (focusIsHidden) control.focus({ preventScroll: true });
  };

  const dismissOffer = () => {
    returningToTeaser = false;
    dismissed = true;
    panelCollapsedByControl = false;
    clearCollapseTimer();
    clearProductOpenTimer();
    writeDismissal();
    hideControl();
    suppressTeaser(true);
  };

  const returnToChevron = () => {
    returningToTeaser = true;
    clearCollapseTimer();
    collapseStarted = false;
    autoCollapsed = false;
    panelCollapsedByControl = false;
    [50, 250, 700].forEach((delay) => window.setTimeout(queueSync, delay));
  };

  const collapsePanel = () => {
    panel = findPanel();
    if (!panel) return;

    const closeButton = panel.querySelector('[aria-label="Close dialog"]');
    if (!closeButton) return;

    panelCollapsedByControl = true;
    collapsingViaControl = true;
    closeButton.click();
    collapsingViaControl = false;
    autoCollapsed = true;
    suppressTeaser(true);
    showControl(false);
    [0, 100, 300].forEach((delay) => window.setTimeout(restoreCollapsedFocus, delay));
    [50, 250, 700].forEach((delay) => window.setTimeout(queueSync, delay));
  };

  const openOffer = (event) => {
    panel = findPanel();
    if (panel) {
      hideControl();
      return;
    }

    panelCollapsedByControl = false;

    teaser = findTeaser() || (teaser && teaser.isConnected ? teaser : null);
    const formId = findFormId() || knownFormId;
    if (formId) knownFormId = formId;
    const trigger = findTeaserTrigger(teaser);
    if (!formId && !trigger) return;

    focusPanelAfterOpen = !event || event.detail === 0;
    hideControl();

    if (formId) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    } else {
      suppressTeaser(false);
      trigger.click();
    }

    suppressTeaser(true);
    [50, 250, 700].forEach((delay) => window.setTimeout(queueSync, delay));
  };

  const beginAutoCollapse = (collapseEvenIfFocused = false) => {
    if (collapseStarted || dismissed || !teaser) return;
    collapseStarted = true;

    const collapseWhenIdle = () => {
      collapseTimer = null;
      if (dismissed || (panel && isVisible(panel))) return;
      if (!collapseEvenIfFocused && teaser && teaser.contains(document.activeElement)) {
        collapseTimer = window.setTimeout(collapseWhenIdle, 500);
        return;
      }
      autoCollapsed = true;
      suppressTeaser(true);
      showControl(false);
    };

    collapseTimer = window.setTimeout(collapseWhenIdle, autoCollapseDelay);
  };

  const sync = () => {
    syncQueued = false;

    if (!mobileQuery.matches) {
      document.documentElement.classList.remove('city-klaviyo-teaser-enhanced');
      clearCollapseTimer();
      collapseStarted = false;
      autoCollapsed = false;
      panelWasSeen = false;
      panelCollapsedByControl = false;
      focusPanelAfterOpen = false;
      hideControl();
      if (teaser) {
        teaser.classList.remove('city-klaviyo-teaser--suppressed');
        teaser.removeAttribute('aria-hidden');
      }
      return;
    }

    teaser = findTeaser() || (teaser && teaser.isConnected ? teaser : null);
    panel = findPanel();
    knownFormId = findFormId() || knownFormId;

    if (!teaser && !panel && !knownFormId) {
      document.documentElement.classList.remove('city-klaviyo-teaser-enhanced');
      clearCollapseTimer();
      collapseStarted = false;
      autoCollapsed = false;
      panelWasSeen = false;
      panelCollapsedByControl = false;
      focusPanelAfterOpen = false;
      hideControl();
      return;
    }

    document.documentElement.classList.add('city-klaviyo-teaser-enhanced');

    if (dismissed) {
      hideControl();
      suppressTeaser(true);
      return;
    }

    if (panel) {
      if (!panel.id) panel.id = panelId;
      control.setAttribute('aria-controls', panel.id);
      panelWasSeen = true;
      clearCollapseTimer();
      suppressTeaser(true);
      hideControl();
      sizeCloseTarget(panel);
      if (focusPanelAfterOpen) {
        focusPanelAfterOpen = false;
        window.setTimeout(() => {
          const closeButton = panel && sizeCloseTarget(panel);
          if (closeButton) closeButton.focus({ preventScroll: true });
        }, 0);
      }
      return;
    }

    if (returningToTeaser) {
      returningToTeaser = false;
      panelWasSeen = false;
      panelCollapsedByControl = false;
      autoCollapsed = false;
      collapseStarted = false;
      suppressTeaser(true);
      showControl(false);
      return;
    }

    if (panelCollapsedByControl || panelWasSeen) {
      autoCollapsed = true;
      suppressTeaser(true);
      showControl(false);
      return;
    }

    if (autoCollapsed) {
      suppressTeaser(true);
      showControl(false);
      return;
    }

    if (teaser && isVisible(teaser)) {
      suppressTeaser(true);
      showControl(false);
    }
  };

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(sync);
  }

  control.addEventListener('click', openOffer);
  dismissControl.addEventListener('click', dismissOffer);

  window.addEventListener('klaviyoForms', (event) => {
    const formId = String(event.detail?.formId || '');
    if (!routedProductFormIds.has(formId)) return;

    if (event.detail?.type === 'submit') dismissOffer();
    if (event.detail?.type === 'close') returnToChevron();
    if (event.detail?.type === 'open') queueSync();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!mobileQuery.matches || dismissed) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const closeDialog = target.closest('[aria-label="Close dialog"]');
      if (closeDialog && closeDialog.closest('[role="dialog"][aria-label="POPUP Form"]')) {
        if (collapsingViaControl) return;
        returnToChevron();
        return;
      }

      const button = target.closest('button');
      if (button && normaliseText(button.textContent) === 'no, thanks') {
        returnToChevron();
      }
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    if (!mobileQuery.matches || dismissed || !panelCollapsedByControl) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const focusIsHidden =
      (teaser && teaser.contains(target)) ||
      (panel && panel.contains(target) && !isVisible(panel));
    if (focusIsHidden) window.setTimeout(restoreCollapsedFocus, 0);
  });

  const observer = new MutationObserver(queueSync);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (typeof mobileQuery.addEventListener === 'function') mobileQuery.addEventListener('change', queueSync);
  else mobileQuery.addListener(queueSync);
  window.addEventListener('resize', queueSync, { passive: true });

  dismissed = readDismissal();
  window.CityKlaviyoTeaserControl = { sync: queueSync };
  queueSync();
  scheduleProductIntentForm();
})();
